import {
	ArrowRightIcon,
	ChartBarIcon,
	CheckCircleIcon,
	DocumentTextIcon,
	EyeIcon,
	ShieldCheckIcon,
	UsersIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

/**
 * Advanced Vision Assessment - Professional Landing Page
 *
 * Tailored for clinicians and ophthalmology experts
 * Led by Dr. Rajeev Muni, Dr. Radha Kohly, and Dr. Shiva Sabour
 */

export default function LandingPage() {
	return (
		<div className="flex-1 bg-white overflow-auto">
			{/* Hero Section */}
			<div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
				{/* Background Pattern */}
				<div className="absolute inset-0 opacity-10">
					<div
						className="absolute inset-0"
						style={{
							backgroundImage:
								"radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
							backgroundSize: "40px 40px",
						}}
					/>
				</div>

				<div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-32">
					<div className="grid lg:grid-cols-2 gap-12 items-center">
						{/* Left Column - Content */}
						<div className="space-y-8">
							<h1 className="text-5xl lg:text-6xl font-bold leading-tight">
								Advanced Vision Assessment Platform
							</h1>

							<p className="text-xl text-blue-100 leading-relaxed">
								A digital tool for simulating and documenting visual impairments
								in clinical and research settings.
							</p>

							<div className="flex flex-col sm:flex-row gap-4">
								<Link
									to="/login"
									className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl"
								>
									Get Started
									<ArrowRightIcon className="w-5 h-5" />
								</Link>
							</div>

							{/* Research Credentials */}
							<div className="pt-8 border-t border-blue-700/50">
								<p className="text-sm text-blue-200 mb-3">Led by:</p>
								<div className="space-y-1 text-sm">
									<p className="text-blue-100">
										Dr. Rajeev Muni · Dr. Radha Kohly · Dr. Shiva Sabour
									</p>
									<p className="text-blue-300 text-xs">
										University of Toronto · Unity Health
									</p>
								</div>
							</div>
						</div>

						{/* Right Column - Screenshot */}
						<div className="relative">
							<div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 shadow-2xl">
								<img
									src="/images/screenshot.png"
									alt="Advanced Vision Assessment Platform Interface"
									className="rounded-lg w-full h-auto"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Features Section */}
			<div className="max-w-7xl mx-auto px-6 py-20">
				<div className="text-center mb-16">
					<h2 className="text-4xl font-bold text-gray-900 mb-4">
						Clinical Capabilities
					</h2>
					<p className="text-xl text-gray-600 max-w-3xl mx-auto">
						Simulate visual impairments with precision tools for clinical
						documentation
					</p>
				</div>

				<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
					{/* Feature Cards */}
					{[
						{
							icon: <EyeIcon className="w-8 h-8" />,
							title: "Visual Field Defects",
							description:
								"Simulate scotomas and field restrictions with customizable parameters",
						},
						{
							icon: <ChartBarIcon className="w-8 h-8" />,
							title: "Metamorphopsia",
							description: "Model distortions and macular abnormalities",
						},
						{
							icon: <DocumentTextIcon className="w-8 h-8" />,
							title: "Clinical Documentation",
							description: "Export detailed reports for medical records",
						},
						{
							icon: <UsersIcon className="w-8 h-8" />,
							title: "Patient Management",
							description:
								"Track multiple patients with session-based assessments",
						},
						{
							icon: <ShieldCheckIcon className="w-8 h-8" />,
							title: "Secure & Private",
							description: "Encrypted data storage with access controls",
						},
						{
							icon: <EyeIcon className="w-8 h-8" />,
							title: "Anisoconia & Diplopia",
							description:
								"Demonstrate size discrepancies and double vision effects",
						},
					].map((feature) => (
						<div
							key={feature.title}
							className="group bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all"
						>
							<div className="inline-flex p-3 rounded-lg bg-blue-100 text-blue-600 mb-4 group-hover:scale-110 transition-transform">
								{feature.icon}
							</div>
							<h3 className="text-xl font-semibold text-gray-900 mb-2">
								{feature.title}
							</h3>
							<p className="text-gray-600 leading-relaxed">
								{feature.description}
							</p>
						</div>
					))}
				</div>
			</div>

			{/* Clinical Tools Section */}
			<div className="bg-gray-50 py-20">
				<div className="max-w-7xl mx-auto px-6">
					<div className="grid lg:grid-cols-2 gap-12 items-center">
						<div>
							<h2 className="text-4xl font-bold text-gray-900 mb-6">
								Precision Tools for Assessment
							</h2>
							<p className="text-lg text-gray-600 mb-8">
								Built for ophthalmologists and vision researchers to accurately
								simulate visual impairments.
							</p>

							<div className="space-y-4">
								{[
									"Real-time image processing with adjustable parameters",
									"Binocular vision simulation (independent left/right eye)",
									"Export high-resolution images and parameter logs",
									"Interactive tools for patient education",
								].map((item) => (
									<div key={item} className="flex items-start gap-3">
										<CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
										<p className="text-gray-700">{item}</p>
									</div>
								))}
							</div>
						</div>

						<div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
							<h3 className="text-2xl font-bold text-gray-900 mb-6">
								Available Simulations
							</h3>
							<div className="grid grid-cols-2 gap-3">
								{[
									"Blurriness",
									"Rotation",
									"Brightness",
									"Contrast",
									"Color Shift",
									"Scotoma",
									"Metamorphopsia",
									"Distortion",
									"Vacuum Effect",
									"Diplopia",
									"Anisoconia",
								].map((tool) => (
									<div
										key={tool}
										className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors"
									>
										<div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
										<span className="text-gray-700 text-sm">{tool}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* CTA Section */}
			<div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-20">
				<div className="max-w-4xl mx-auto px-6 text-center">
					<h2 className="text-4xl font-bold mb-6">
						Ready to Enhance Your Clinical Practice?
					</h2>
					<p className="text-xl text-blue-100 mb-8">
						Join ophthalmology researchers and clinicians using Advanced Vision
						Assessment.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Link
							to="/login"
							className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all shadow-xl"
						>
							Sign In
							<ArrowRightIcon className="w-5 h-5" />
						</Link>
					</div>
				</div>
			</div>

			{/* Footer */}
			<div className="bg-gray-900 text-gray-400 py-12">
				<div className="max-w-7xl mx-auto px-6">
					<div className="grid md:grid-cols-3 gap-8 mb-8">
						<div>
							<h4 className="text-white font-semibold mb-4">
								Advanced Vision Assessment
							</h4>
							<p className="text-sm leading-relaxed">
								A clinical research tool developed at the University of Toronto
								for ophthalmology education and research.
							</p>
						</div>
						<div>
							<h4 className="text-white font-semibold mb-4">Leadership Team</h4>
							<ul className="space-y-2 text-sm">
								<li>
									<a
										href="https://ophthalmology.utoronto.ca/faculty/rajeev-muni"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										Dr. Rajeev Muni MD, FRCS(C)
									</a>
								</li>
								<li>
									<a
										href="https://sunnybrook.ca/team/member.asp?t=2&page=483&m=444"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										Dr. Radha P Kohly, MD, PhD, FRCSC
									</a>
								</li>
								<li>
									<a
										href="https://www.researchgate.net/profile/Shiva-Sabour"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										Dr. Shiva Sabour
									</a>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="text-white font-semibold mb-4">Platform</h4>
							<ul className="space-y-2 text-sm">
								<li>
									<Link
										to="/login"
										className="hover:text-white transition-colors"
									>
										Sign In
									</Link>
								</li>
								<li>
									<a
										href="https://getqatalyst.com/advanced-vision-assessment"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-white transition-colors"
									>
										About
									</a>
								</li>
							</ul>
						</div>
					</div>
					<div className="border-t border-gray-800 pt-8 text-center text-sm">
						<p>
							© {new Date().getFullYear()} Advanced Vision Assessment. Developed
							for clinical research and education by{" "}
							<a
								href="https://getqatalyst.com"
								target="_blank"
								rel="noopener noreferrer"
								className="hover:text-white transition-colors"
							>
								Qatalyst Labs.
							</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
